<script setup>
import {ref, computed, getCurrentInstance} from 'vue'
import {useReportOutput} from '@/components/report/outputs/useReportOutput'
import helpers from '@/lib/helpers'
import TagEditModal from '@/components/layout/tag-edit-modal.vue'

const props = defineProps({
  value: null,
  item: Object,
})

const dialog = getCurrentInstance().proxy.$dialog
const {v, extra} = useReportOutput(props)

// keyed by the record type the report puts in `extra.type`. A type with no
// entry still renders its badges - it just cannot be edited from the cell.
const WRITE_PERM = {
  lead: 'events.leads.tags.write',
  customer: 'customers.tags.write',
  vendor: 'vendors.tags.write',
  auction: 'auctions.tags.write',
}
const READ_PERM = {
  lead: 'events.leads.tags.read',
  customer: 'customers.tags.read',
  vendor: 'vendors.tags.read',
  auction: 'auctions.tags.read',
}

const kind = computed(() => (extra.value?.type || '').toLowerCase())
const tagUrl = computed(() => extra.value?.tag_url)
const writePerm = computed(() => WRITE_PERM[kind.value])
const readPerm = computed(() => READ_PERM[kind.value])
const canEdit = computed(() => !!tagUrl.value && !!writePerm.value && helpers.can(writePerm.value))

// re-fetched csv after an edit, so the cell reflects the change without a full report re-run
const localCsv = ref(null)
const csv = computed(() => localCsv.value ?? v.value ?? '')
const tags = computed(() => String(csv.value || '').split(',').map(t => t.trim()).filter(Boolean))

const edit = async () => {
  await dialog.showAndWait(TagEditModal, {
    url: tagUrl.value,
    title: extra.value?.gli,
    write: writePerm.value,
    read: readPerm.value,
  })

  try {
    const list = await helpers.asyncAction(tagUrl.value, 'GET', {noload: true})
    localCsv.value = (list || []).map(t => t.tag).sort().join(', ')
  } catch (e) {
    // best-effort refresh only
  }
}
</script>

<template>
  <div class="d-flex align-items-center flex-wrap" style="gap:.15rem; min-height: 24px">
    <b-badge v-for="t in tags" :key="t" variant="secondary" class="mr-1 mb-1">{{ t }}</b-badge>
    <span v-if="!tags.length" class="text-muted">&mdash;</span>
    <b-link v-if="canEdit && !$store.state.printing" class="ml-1" @click="edit"
            v-b-tooltip.hover title="Edit tags">
      <font-awesome-icon :icon="['fas', 'tag']"/>
    </b-link>
  </div>
</template>
